import { useState, useMemo, useCallback, useRef } from "react";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
} from "@/components/ui/SidePanel";
import {
  SidePanelStack,
  SidePanelStackContent,
  useSidePanelStack,
} from "@/components/ui/SidePanelStack";
import type { Club, UserProfile, ClubLink, ClubFile } from "@/api/clubsApi";
import {
  useFetchClubMembersQuery,
  useFetchUserProfilesByIdsQuery,
  useDeleteClubMemberMutation,
  useUpdateClubMutation,
  useFetchCurrentUserProfileQuery,
  useUpdateNotificationPreferencesMutation,
  useFetchClubInfoQuery,
  useCreateClubLinkMutation,
  useDeleteClubLinkMutation,
  useDeleteClubFileMutation,
} from "@/api/clubsApi";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { Skeleton } from "@/components/ui/Skeleton";
import { LocationView } from "@/components/location";
import { userDisplayName } from "@tinyclub/shared/utils";
import { uploadFile } from "@/utils/uploadUtils";
import { ClubDataExportView } from "./ClubDataExportView";
import { useCreateClubFileMutation } from "@/api/clubsApi";

interface ClubInfoPanelProps {
  club: Club;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUserId?: string;
}

/**
 * ClubInfoPanel - Side panel containing club info:
 * - About section
 * - Guidelines section
 * - Location section (with map)
 * - Admin info (for owners/cohosts)
 * - Can navigate to full members list within the panel
 */
export function ClubInfoPanel({
  club,
  open,
  onOpenChange,
  currentUserId,
}: ClubInfoPanelProps) {
  // Fetch club members
  const { data: members, isLoading: membersLoading } = useFetchClubMembersQuery(
    { clubId: club.id!, includeUserProfiles: false },
    { skip: !club.id || !open }
  );

  // Get member IDs for fetching profiles (limit to first 6 for info view display)
  const previewMemberIds = useMemo(() => {
    if (!members) return [];
    return members
      .slice(0, 6)
      .map((m) => m.user_id)
      .filter(Boolean) as string[];
  }, [members]);

  // Get all member IDs for full members view
  const allMemberIds = useMemo(() => {
    if (!members) return [];
    return members.map((m) => m.user_id).filter(Boolean) as string[];
  }, [members]);

  // Fetch profiles for the preview members
  const { data: previewProfiles = [] } = useFetchUserProfilesByIdsQuery(
    previewMemberIds,
    { skip: previewMemberIds.length === 0 || !open }
  );

  // Fetch all profiles (needed for members view)
  const { data: allProfiles = [], isLoading: profilesLoading } =
    useFetchUserProfilesByIdsQuery(allMemberIds, {
      skip: allMemberIds.length === 0 || !open,
    });

  // Get current user's role
  const currentUserMember = members?.find((m) => m.user_id === currentUserId);
  const isOwnerOrCohost =
    currentUserMember?.role === "owner" || currentUserMember?.role === "cohost";

  const totalMemberCount = members?.length ?? 0;
  const showViewAllButton = totalMemberCount > 6;

  // Create initial view for the stack
  const initialView = useMemo(
    () => ({
      id: "info",
      title: "Club info",
      content: (
        <SidePanelBody className="pt-0">
          <InfoViewContent
            club={club}
            memberProfiles={previewProfiles}
            totalMemberCount={totalMemberCount}
            showViewAllButton={showViewAllButton}
            isOwnerOrCohost={isOwnerOrCohost}
            currentUserRole={currentUserMember?.role}
            currentUserId={currentUserId}
            members={members ?? []}
            allProfiles={allProfiles}
            isLoadingProfiles={membersLoading || profilesLoading}
            onClose={() => onOpenChange(false)}
          />
        </SidePanelBody>
      ),
    }),
    [
      club,
      previewProfiles,
      totalMemberCount,
      showViewAllButton,
      isOwnerOrCohost,
      currentUserMember?.role,
      currentUserId,
      members,
      allProfiles,
      membersLoading,
      profilesLoading,
      onOpenChange,
    ]
  );

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelStack initialView={initialView}>
          {({ currentView, canGoBack, onBack }) => (
            <>
              <SidePanelHeader
                title={currentView.title}
                onBack={canGoBack ? onBack : undefined}
                onClose={!canGoBack ? () => onOpenChange(false) : undefined}
              />
              <SidePanelStackContent />
            </>
          )}
        </SidePanelStack>
      </SidePanelContent>
    </SidePanel>
  );
}

// Info view content (uses the stack context to push to members view)
function InfoViewContent({
  club,
  memberProfiles,
  totalMemberCount,
  showViewAllButton,
  isOwnerOrCohost,
  currentUserRole,
  currentUserId,
  members,
  allProfiles,
  isLoadingProfiles,
  onClose,
}: {
  club: Club;
  memberProfiles: UserProfile[];
  totalMemberCount: number;
  showViewAllButton: boolean;
  isOwnerOrCohost: boolean;
  currentUserRole?: string;
  currentUserId?: string;
  members: Array<{ user_id: string; role?: string }>;
  allProfiles: UserProfile[];
  isLoadingProfiles: boolean;
  onClose: () => void;
}) {
  const { push } = useSidePanelStack();
  const navigate = useNavigate();
  const [deleteClubMember, { isLoading: isLeaving }] = useDeleteClubMemberMutation();
  const [updateClub, { isLoading: isSavingGuidelines }] = useUpdateClubMutation();
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [isEditingGuidelines, setIsEditingGuidelines] = useState(false);
  const [guidelinesText, setGuidelinesText] = useState(club.guidelines || "");

  const isMember = !!currentUserId && members.some((m) => m.user_id === currentUserId);
  const isOwner = currentUserRole === "owner";

  const handleLeaveClub = async () => {
    if (!currentUserId || !club.id) return;
    try {
      await deleteClubMember({ clubId: club.id, userId: currentUserId }).unwrap();
      onClose();
      navigate({ to: "/clubs" });
    } catch (error) {
      console.error("Failed to leave club:", error);
    }
  };

  const handleViewAllMembers = useCallback(() => {
    push({
      id: "members",
      title: "Members",
      content: (
        <MembersView
          members={members}
          profiles={allProfiles}
          isLoading={isLoadingProfiles}
        />
      ),
    });
  }, [push, members, allProfiles, isLoadingProfiles]);

  const handleViewLinksFiles = useCallback(() => {
    if (!club.id || !currentUserId) return;
    push({
      id: "links-files",
      title: "Links & Files",
      content: (
        <LinksFilesView
          clubId={club.id}
          currentUserId={currentUserId}
          isOwnerOrCohost={isOwnerOrCohost}
        />
      ),
    });
  }, [push, club.id, currentUserId, isOwnerOrCohost]);

  const handleExportMyData = useCallback(() => {
    push({
      id: "export-self",
      title: "Export My Data",
      content: <ClubDataExportView club={club} scope="self" />,
    });
  }, [push, club]);

  return (
    <div className="space-y-6 pb-4">
      {/* Members Preview */}
      {memberProfiles.length > 0 && (
        <section className="bg-surface rounded-2xl p-5">
          <h3 className="text-body-2-medium text-text-secondary mb-3">
            Members ({totalMemberCount})
          </h3>
          <div className="grid grid-cols-3 gap-4">
            {memberProfiles.map((profile) => (
              <MemberCard key={profile.id} profile={profile} />
            ))}
          </div>
          {showViewAllButton && (
            <button
              onClick={handleViewAllMembers}
              className="w-full mt-4 py-3 text-body-2-medium text-text-primary hover:bg-surface-1 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              View all members
              <Icon name="chevron-right" size={14} className="text-text-secondary" />
            </button>
          )}
        </section>
      )}

      {/* Description */}
      {club.description && (
        <section className="bg-surface rounded-2xl p-4">
          <h3 className="text-body-2-medium text-text-secondary mb-2">About</h3>
          <p className="text-body-2 text-text-primary">{club.description}</p>
        </section>
      )}

      {/* Guidelines */}
      {(club.guidelines || isOwnerOrCohost) && (
        <section className="bg-surface rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-body-2-medium text-text-secondary">
              Guidelines
            </h3>
            {isOwnerOrCohost && !isEditingGuidelines && (
              <button
                type="button"
                onClick={() => {
                  setGuidelinesText(club.guidelines || "");
                  setIsEditingGuidelines(true);
                }}
                className="text-body-3-medium text-text-secondary hover:text-text-primary transition-colors"
              >
                {club.guidelines ? "Edit" : "Add"}
              </button>
            )}
          </div>
          {isEditingGuidelines ? (
            <div className="space-y-3">
              <textarea
                value={guidelinesText}
                onChange={(e) => setGuidelinesText(e.target.value)}
                placeholder="Add guidelines for your club members..."
                className="w-full min-h-[120px] p-3 rounded-xl bg-surface-1 text-body-2 text-text-primary placeholder:text-text-tertiary resize-y focus:outline-none"
                autoFocus
                disabled={isSavingGuidelines}
              />
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setIsEditingGuidelines(false)}
                  disabled={isSavingGuidelines}
                  className="px-3 py-1.5 rounded-full text-body-3-medium text-text-secondary hover:bg-surface-1 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isSavingGuidelines}
                  onClick={async () => {
                    if (!club.id) return;
                    try {
                      await updateClub({
                        id: club.id,
                        guidelines: guidelinesText.trim() || undefined,
                      }).unwrap();
                      setIsEditingGuidelines(false);
                    } catch (error) {
                      console.error("Failed to save guidelines:", error);
                    }
                  }}
                  className="px-3 py-1.5 rounded-full bg-olive text-white text-body-3-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {isSavingGuidelines ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          ) : club.guidelines ? (
            <p className="text-body-2 text-text-primary whitespace-pre-wrap">
              {club.guidelines}
            </p>
          ) : (
            <p className="text-body-3 text-text-tertiary">
              No guidelines have been added yet.
            </p>
          )}
        </section>
      )}

      {/* Notification Preferences — visible to all members */}
      {isMember && club.id && currentUserId && (
        <NotificationPreferencesSection
          clubId={club.id}
          userId={currentUserId}
        />
      )}

      {/* Links & Files — visible to all members */}
      {isMember && club.id && currentUserId && (
        <section className="bg-surface rounded-2xl overflow-hidden">
          <button
            onClick={handleViewLinksFiles}
            className="w-full flex items-center gap-3 p-4 hover:bg-surface-1 transition-colors text-left"
          >
            <Icon name="link" size={16} className="text-text-secondary" />
            <span className="text-body-2 text-text-primary flex-1">
              Links & Files
            </span>
            <Icon name="angle-right" size={14} className="text-text-secondary" />
          </button>
        </section>
      )}

      {/* Export my data — visible to all members */}
      {isMember && club.id && (
        <section className="bg-surface rounded-2xl overflow-hidden">
          <button
            onClick={handleExportMyData}
            className="w-full flex items-center gap-3 p-4 hover:bg-surface-1 transition-colors text-left"
          >
            <Icon
              name="arrow-down-to-bracket"
              size={16}
              className="text-text-secondary"
            />
            <span className="text-body-2 text-text-primary flex-1">
              Export my data
            </span>
            <Icon name="angle-right" size={14} className="text-text-secondary" />
          </button>
        </section>
      )}

      {/* Location */}
      {club.location_object && (
        <section>
          <LocationView
            location={club.location_object}
            locationName={club.location_name}
          />
        </section>
      )}

      {/* Admin info */}
      {isOwnerOrCohost && (
        <section className="pt-4 border-t border-border-tertiary">
          <p className="text-body-3 text-text-tertiary">
            You are{" "}
            {currentUserRole === "owner" ? "the owner" : "a co-host"} of this
            club.
          </p>
        </section>
      )}

      {/* Leave club button — shown for members who are not the owner */}
      {isMember && !isOwner && (
        <section className="pt-4">
          {!showLeaveConfirm ? (
            <button
              onClick={() => setShowLeaveConfirm(true)}
              className="w-full p-3 rounded-full border border-border-tertiary text-[18px] font-medium text-text-primary hover:bg-surface-1 transition-colors"
            >
              Leave club
            </button>
          ) : (
            <div className="bg-surface rounded-2xl p-4 space-y-3">
              <p className="text-body-1-medium text-text-primary">Leave this club?</p>
              <p className="text-body-2 text-text-secondary">
                You can rejoin later if the club is public or if you're invited again.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLeaveConfirm(false)}
                  className="flex-1 p-3 rounded-full border border-border-tertiary text-[18px] font-medium text-text-primary hover:bg-surface-1 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLeaveClub}
                  disabled={isLeaving}
                  className="flex-1 p-3 rounded-full border border-border-tertiary text-[18px] font-medium text-accent-negative hover:bg-surface-1 transition-colors disabled:opacity-50"
                >
                  {isLeaving ? "Leaving..." : "Leave club"}
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// Members view component (full list)
function MembersView({
  members,
  profiles,
  isLoading,
}: {
  members: Array<{ user_id: string; role?: string }>;
  profiles: UserProfile[];
  isLoading: boolean;
}) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  // Create a map of user ID to profile
  const profileMap = useMemo(() => {
    return new Map(profiles.map((p) => [p.id, p]));
  }, [profiles]);

  // Separate members into owners, cohosts, and regular members
  const { ownerProfiles, cohostProfiles, regularMemberProfiles } = useMemo(() => {
    const owners: UserProfile[] = [];
    const cohosts: UserProfile[] = [];
    const regularMembers: UserProfile[] = [];

    members.forEach((member) => {
      const profile = profileMap.get(member.user_id);
      if (!profile) return;

      if (member.role === "owner") {
        owners.push(profile);
      } else if (member.role === "cohost") {
        cohosts.push(profile);
      } else {
        regularMembers.push(profile);
      }
    });

    return {
      ownerProfiles: owners,
      cohostProfiles: cohosts,
      regularMemberProfiles: regularMembers,
    };
  }, [members, profileMap]);

  // Filter function based on search query
  const filterProfiles = useCallback(
    (profiles: UserProfile[]) => {
      if (!searchQuery.trim()) return profiles;

      const query = searchQuery.toLowerCase();
      return profiles.filter((profile) => {
        const displayName = userDisplayName(profile).toLowerCase();
        const username = profile.username?.toLowerCase() || "";
        return displayName.includes(query) || username.includes(query);
      });
    },
    [searchQuery]
  );

  // Apply filtering to each section
  const filteredHosts = useMemo(
    () => filterProfiles([...ownerProfiles, ...cohostProfiles]),
    [ownerProfiles, cohostProfiles, filterProfiles]
  );
  const filteredMembers = useMemo(
    () => filterProfiles(regularMemberProfiles),
    [regularMemberProfiles, filterProfiles]
  );

  // Build flat list for keyboard navigation (hosts first, then members)
  const allFilteredProfiles = useMemo(
    () => [...filteredHosts, ...filteredMembers],
    [filteredHosts, filteredMembers]
  );

  // Get role for a profile
  const getRoleForProfile = (profileId: string): string | null => {
    const member = members.find((m) => m.user_id === profileId);
    if (member?.role === "owner") return "Owner";
    if (member?.role === "cohost") return "Co-host";
    return null;
  };

  // Handle member click
  const handleMemberClick = (userId: string) => {
    navigate({ to: "/profile/$userId", params: { userId } });
  };

  const { highlightedIndex, onKeyDown } = useKeyboardNavigation({
    items: allFilteredProfiles,
    onSelect: (profile) => handleMemberClick(profile.id),
    enabled: !!searchQuery.trim(),
  });

  const hasResults = filteredHosts.length > 0 || filteredMembers.length > 0;

  return (
    <>
      {/* Search Bar */}
      <div className="flex-shrink-0 px-5 pb-4">
        <div className="flex items-center gap-3 px-4 py-3 rounded-full border border-border-tertiary bg-surface">
          <Icon name="search" size={16} className="text-text-secondary" />
          <input
            type="text"
            placeholder="Search members"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={onKeyDown}
            className="flex-1 bg-transparent text-body-2 text-text-primary placeholder:text-text-secondary focus:outline-none"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="p-1 hover:opacity-70 transition-opacity"
            >
              <Icon name="xmark" size={16} className="text-text-tertiary" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Content */}
      <SidePanelBody className="pt-0">
        {isLoading ? (
          <MembersListSkeleton />
        ) : !hasResults && searchQuery ? (
          <div className="text-center py-12">
            <p className="text-body-2 text-text-secondary">
              No members matching "{searchQuery}"
            </p>
          </div>
        ) : (
          <div className="space-y-6 pb-4">
            {/* Hosts Section */}
            {filteredHosts.length > 0 && (
              <MemberSection
                title="Hosts"
                profiles={filteredHosts}
                getRoleForProfile={getRoleForProfile}
                onMemberClick={handleMemberClick}
                startIndex={0}
                highlightedIndex={highlightedIndex}
              />
            )}

            {/* Members Section */}
            {filteredMembers.length > 0 && (
              <MemberSection
                title="Members"
                profiles={filteredMembers}
                getRoleForProfile={getRoleForProfile}
                onMemberClick={handleMemberClick}
                startIndex={filteredHosts.length}
                highlightedIndex={highlightedIndex}
              />
            )}
          </div>
        )}
      </SidePanelBody>
    </>
  );
}

// Member section component
function MemberSection({
  title,
  profiles,
  getRoleForProfile,
  onMemberClick,
  startIndex = 0,
  highlightedIndex = -1,
}: {
  title: string;
  profiles: UserProfile[];
  getRoleForProfile: (profileId: string) => string | null;
  onMemberClick: (userId: string) => void;
  startIndex?: number;
  highlightedIndex?: number;
}) {
  return (
    <section>
      <h2 className="text-h6 text-text-primary mb-3">{title}</h2>
      <div className="space-y-1">
        {profiles.map((profile, i) => (
          <MemberRow
            key={profile.id}
            profile={profile}
            role={getRoleForProfile(profile.id)}
            onClick={() => onMemberClick(profile.id)}
            navIndex={startIndex + i}
            highlighted={startIndex + i === highlightedIndex}
          />
        ))}
      </div>
    </section>
  );
}

// Individual member row
function MemberRow({
  profile,
  role,
  onClick,
  navIndex,
  highlighted = false,
}: {
  profile: UserProfile;
  role: string | null;
  onClick: () => void;
  navIndex?: number;
  highlighted?: boolean;
}) {
  const displayName = userDisplayName(profile);

  return (
    <button
      onClick={onClick}
      data-nav-index={navIndex}
      className={`w-full flex items-center gap-3 p-3 rounded-xl hover:bg-surface transition-colors cursor-pointer text-left${highlighted ? " bg-surface" : ""}`}
    >
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border border-border-tertiary">
        {profile.avatar_medium || profile.avatar_small ? (
          <img
            src={profile.avatar_medium || profile.avatar_small || ""}
            alt={displayName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-surface-1 flex items-center justify-center">
            <Icon name="user" size={16} className="text-text-tertiary" />
          </div>
        )}
      </div>

      {/* Name */}
      <div className="flex-1 min-w-0">
        <p className="text-body-2-medium text-text-primary truncate">
          {displayName}
        </p>
      </div>

      {/* Role badge */}
      {role && (
        <span className="px-2 py-1 rounded-lg bg-surface-1 text-body-3 text-text-secondary flex-shrink-0">
          {role}
        </span>
      )}
    </button>
  );
}

// Member card for displaying in the info panel preview
function MemberCard({ profile }: { profile: UserProfile }) {
  const displayName = userDisplayName(profile);

  return (
    <Link
      to="/profile/$userId"
      params={{ userId: profile.id }}
      className="flex flex-col items-center hover:opacity-80 transition-opacity"
    >
      {/* Avatar */}
      <div className="w-16 h-16 rounded-full overflow-hidden border border-border-tertiary mb-2">
        {profile.avatar_medium || profile.avatar_small ? (
          <img
            src={profile.avatar_medium || profile.avatar_small || ""}
            alt={displayName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-surface-1 flex items-center justify-center">
            <Icon name="user" size={24} className="text-text-tertiary" />
          </div>
        )}
      </div>
      {/* Name */}
      <p className="text-body-3 text-text-secondary text-center truncate w-full">
        {displayName}
      </p>
    </Link>
  );
}

// Skeleton loader
function MembersListSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-5 w-14 mb-3" />
        <div className="space-y-1">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-[56px] w-full rounded-xl" />
          ))}
        </div>
      </div>
      <div>
        <Skeleton className="h-5 w-20 mb-3" />
        <div className="space-y-1">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-[56px] w-full rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

// Notification preferences section for club members
type NotificationOption = "everything" | "important" | "none";

interface RadioOption {
  value: NotificationOption;
  label: string;
}

const eventsOptions: RadioOption[] = [
  { value: "everything", label: "Everything" },
  { value: "important", label: "Reminders only" },
  { value: "none", label: "Nothing" },
];

const postsOptions: RadioOption[] = [
  { value: "everything", label: "Everything" },
  { value: "important", label: "Comments and likes only" },
  { value: "none", label: "Nothing" },
];

function NotificationPreferencesSection({
  clubId,
  userId,
}: {
  clubId: string;
  userId: string;
}) {
  const { data: profile } = useFetchCurrentUserProfileQuery(userId);
  const [updatePreferences] = useUpdateNotificationPreferencesMutation();

  const clubPrefs = profile?.notification_preferences?.clubs?.[clubId];

  // Map DB values to UI values
  const eventsValue: NotificationOption = clubPrefs?.events === "reminders_only"
    ? "important"
    : clubPrefs?.events === "nothing"
      ? "none"
      : "everything";

  const postsValue: NotificationOption = clubPrefs?.posts === "comments_likes_only"
    ? "important"
    : clubPrefs?.posts === "nothing"
      ? "none"
      : "everything";

  const [isUpdatingEvents, setIsUpdatingEvents] = useState(false);
  const [isUpdatingPosts, setIsUpdatingPosts] = useState(false);

  const handleEventsChange = async (value: NotificationOption) => {
    setIsUpdatingEvents(true);
    try {
      const dbValue = value === "important" ? "reminders_only" : value === "none" ? "nothing" : "everything";
      await updatePreferences({
        userId,
        clubId,
        preferences: { events: dbValue },
      }).unwrap();
    } catch (error) {
      console.error("Failed to update events preferences:", error);
    } finally {
      setIsUpdatingEvents(false);
    }
  };

  const handlePostsChange = async (value: NotificationOption) => {
    setIsUpdatingPosts(true);
    try {
      const dbValue = value === "important" ? "comments_likes_only" : value === "none" ? "nothing" : "everything";
      await updatePreferences({
        userId,
        clubId,
        preferences: { posts: dbValue },
      }).unwrap();
    } catch (error) {
      console.error("Failed to update posts preferences:", error);
    } finally {
      setIsUpdatingPosts(false);
    }
  };

  return (
    <section className="bg-surface rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon name="bell-regular" size={16} className="text-text-secondary" />
        <h3 className="text-body-2-medium text-text-secondary">
          Notifications
        </h3>
      </div>

      <div className="space-y-4">
        <NotificationRadioGroup
          title="Hangs and events"
          options={eventsOptions}
          value={eventsValue}
          onChange={handleEventsChange}
          isUpdating={isUpdatingEvents}
        />
        <NotificationRadioGroup
          title="Posts"
          options={postsOptions}
          value={postsValue}
          onChange={handlePostsChange}
          isUpdating={isUpdatingPosts}
        />
      </div>
    </section>
  );
}

function NotificationRadioGroup({
  title,
  options,
  value,
  onChange,
  isUpdating,
}: {
  title: string;
  options: RadioOption[];
  value: NotificationOption;
  onChange: (value: NotificationOption) => void;
  isUpdating: boolean;
}) {
  return (
    <div style={{ opacity: isUpdating ? 0.5 : 1 }} className="transition-opacity">
      <p className="text-body-3-medium text-text-primary mb-2">{title}</p>
      <div className="bg-surface-1 rounded-xl p-3 space-y-3">
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              disabled={isUpdating}
              className="w-full flex items-center justify-between py-1 text-left disabled:cursor-not-allowed"
            >
              <span className="text-body-2 text-text-primary">
                {option.label}
              </span>
              <Icon
                name={isSelected ? "circle-dot" : "circle"}
                size={18}
                className="text-text-secondary"
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Links & Files view (pushed onto SidePanelStack)
function LinksFilesView({
  clubId,
  currentUserId,
  isOwnerOrCohost,
}: {
  clubId: string;
  currentUserId: string;
  isOwnerOrCohost: boolean;
}) {
  const { data: clubInfo, isLoading } = useFetchClubInfoQuery(clubId);
  const [deleteLink] = useDeleteClubLinkMutation();
  const [deleteFile] = useDeleteClubFileMutation();
  const [createLink, { isLoading: isCreatingLink }] = useCreateClubLinkMutation();
  const [createFile] = useCreateClubFileMutation();

  const [searchQuery, setSearchQuery] = useState("");
  const [showAddLink, setShowAddLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkName, setLinkName] = useState("");
  const [linkError, setLinkError] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [fileUploadProgress, setFileUploadProgress] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = "";

    try {
      setFileUploadProgress(0);
      const result = await uploadFile(file, setFileUploadProgress);
      await createFile({
        club_id: clubId,
        name: file.name,
        file_url: result.fileUrl,
        filename: result.filename,
        mime_type: file.type || "application/octet-stream",
        creator_id: currentUserId,
      }).unwrap();
    } catch (error) {
      console.error("Failed to upload file:", error);
    } finally {
      setFileUploadProgress(null);
    }
  };

  const links = clubInfo?.links || [];
  const files = clubInfo?.files || [];

  const filteredLinks = useMemo(() => {
    if (!searchQuery.trim()) return links;
    const q = searchQuery.toLowerCase();
    return links.filter(
      (l) =>
        (l.name?.toLowerCase() || "").includes(q) ||
        l.url.toLowerCase().includes(q)
    );
  }, [links, searchQuery]);

  const filteredFiles = useMemo(() => {
    if (!searchQuery.trim()) return files;
    const q = searchQuery.toLowerCase();
    return files.filter((f) => f.name.toLowerCase().includes(q));
  }, [files, searchQuery]);

  const handleAddLink = async () => {
    const url = linkUrl.trim();
    if (!url || url.length < 4 || !url.includes(".")) {
      setLinkError("Please enter a valid URL");
      return;
    }

    const finalUrl = url.startsWith("http://") || url.startsWith("https://")
      ? url
      : `https://${url}`;

    try {
      await createLink({
        club_id: clubId,
        name: linkName.trim() || null,
        url: finalUrl,
        creator_id: currentUserId,
      }).unwrap();
      setShowAddLink(false);
      setLinkUrl("");
      setLinkName("");
      setLinkError("");
    } catch (error) {
      setLinkError("Failed to add link");
    }
  };

  const handleDeleteLink = async (link: ClubLink) => {
    setDeletingId(link.id);
    try {
      await deleteLink({ id: link.id, club_id: clubId }).unwrap();
    } catch (error) {
      console.error("Failed to delete link:", error);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteFile = async (file: ClubFile) => {
    setDeletingId(file.id);
    try {
      await deleteFile({ id: file.id, club_id: clubId }).unwrap();
    } catch (error) {
      console.error("Failed to delete file:", error);
    } finally {
      setDeletingId(null);
    }
  };

  const hasContent = links.length > 0 || files.length > 0;

  return (
    <SidePanelBody className="pt-0">
      <div className="space-y-4 pb-4">
        {/* Search */}
        {hasContent && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-full border border-border-tertiary bg-surface">
            <Icon name="search" size={16} className="text-text-secondary" />
            <input
              type="text"
              placeholder="Search links & files"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-body-2 text-text-primary placeholder:text-text-secondary focus:outline-none"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="p-1 hover:opacity-70 transition-opacity"
              >
                <Icon name="xmark" size={16} className="text-text-tertiary" />
              </button>
            )}
          </div>
        )}

        {/* Add Link / Add File buttons (for owners/cohosts) */}
        {isOwnerOrCohost && !showAddLink && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddLink(true)}
              className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border border-dashed border-border-tertiary text-body-2-medium text-text-secondary hover:bg-surface-1 transition-colors"
            >
              <Icon name="plus" size={14} />
              Add link
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={fileUploadProgress !== null}
              className="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border border-dashed border-border-tertiary text-body-2-medium text-text-secondary hover:bg-surface-1 transition-colors disabled:opacity-50"
            >
              {fileUploadProgress !== null ? (
                <>
                  <div className="w-4 h-4 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin" />
                  {Math.round(fileUploadProgress * 100)}%
                </>
              ) : (
                <>
                  <Icon name="plus" size={14} />
                  Add file
                </>
              )}
            </button>
            <input
              ref={fileInputRef as React.RefObject<HTMLInputElement>}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}

        {/* Add Link form */}
        {showAddLink && (
          <div className="bg-surface rounded-2xl p-4 space-y-3">
            <div>
              <input
                type="text"
                placeholder="Link name (optional)"
                value={linkName}
                onChange={(e) => setLinkName(e.target.value)}
                className="w-full p-3 rounded-xl bg-surface-1 text-body-2 text-text-primary placeholder:text-text-tertiary focus:outline-none"
              />
            </div>
            <div>
              <input
                type="url"
                placeholder="URL"
                value={linkUrl}
                onChange={(e) => {
                  setLinkUrl(e.target.value);
                  setLinkError("");
                }}
                className="w-full p-3 rounded-xl bg-surface-1 text-body-2 text-text-primary placeholder:text-text-tertiary focus:outline-none"
                autoFocus
              />
              {linkError && (
                <p className="text-body-3 text-accent-negative mt-1">{linkError}</p>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowAddLink(false);
                  setLinkUrl("");
                  setLinkName("");
                  setLinkError("");
                }}
                className="px-3 py-1.5 rounded-full text-body-3-medium text-text-secondary hover:bg-surface-1 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLink}
                disabled={isCreatingLink || !linkUrl.trim()}
                className="px-3 py-1.5 rounded-full bg-olive text-white text-body-3-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {isCreatingLink ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 w-full rounded-xl" />
            ))}
          </div>
        ) : !hasContent ? (
          <div className="text-center py-12">
            <Icon name="link" size={32} className="text-text-tertiary mx-auto mb-3" />
            <p className="text-body-2 text-text-secondary">
              No links or files yet
            </p>
          </div>
        ) : (
          <>
            {/* Links section */}
            {filteredLinks.length > 0 && (
              <section>
                <h3 className="text-body-2-medium text-text-secondary mb-2">
                  Links
                </h3>
                <div className="bg-surface rounded-2xl overflow-hidden">
                  {filteredLinks.map((link, index) => (
                    <div key={link.id}>
                      {index > 0 && (
                        <div className="h-px bg-border-subtle ml-12" />
                      )}
                      <div className="flex items-center gap-3 p-3">
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                        >
                          <div className="w-8 h-8 rounded-lg bg-surface-1 flex items-center justify-center flex-shrink-0">
                            <Icon name="globe" size={14} className="text-text-secondary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-body-2-medium text-text-primary truncate">
                              {link.name || link.url}
                            </p>
                            {link.name && (
                              <p className="text-body-3 text-text-tertiary truncate">
                                {link.url}
                              </p>
                            )}
                          </div>
                          <Icon name="arrow-up-right-from-square" size={12} className="text-text-tertiary flex-shrink-0" />
                        </a>
                        {isOwnerOrCohost && (
                          <button
                            onClick={() => handleDeleteLink(link)}
                            disabled={deletingId === link.id}
                            className="p-2 rounded-lg hover:bg-surface-1 transition-colors text-text-tertiary hover:text-accent-negative disabled:opacity-50 flex-shrink-0"
                          >
                            <Icon name="trash" size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Files section */}
            {filteredFiles.length > 0 && (
              <section>
                <h3 className="text-body-2-medium text-text-secondary mb-2">
                  Files
                </h3>
                <div className="bg-surface rounded-2xl overflow-hidden">
                  {filteredFiles.map((file, index) => (
                    <div key={file.id}>
                      {index > 0 && (
                        <div className="h-px bg-border-subtle ml-12" />
                      )}
                      <div className="flex items-center gap-3 p-3">
                        <a
                          href={file.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
                        >
                          <div className="w-8 h-8 rounded-lg bg-surface-1 flex items-center justify-center flex-shrink-0">
                            <Icon name="file-lines" size={14} className="text-text-secondary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-body-2-medium text-text-primary truncate">
                              {file.name}
                            </p>
                            {file.upload_date && (
                              <p className="text-body-3 text-text-tertiary">
                                {new Date(file.upload_date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <Icon name="arrow-up-right-from-square" size={12} className="text-text-tertiary flex-shrink-0" />
                        </a>
                        {isOwnerOrCohost && (
                          <button
                            onClick={() => handleDeleteFile(file)}
                            disabled={deletingId === file.id}
                            className="p-2 rounded-lg hover:bg-surface-1 transition-colors text-text-tertiary hover:text-accent-negative disabled:opacity-50 flex-shrink-0"
                          >
                            <Icon name="trash" size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </SidePanelBody>
  );
}
